import React, { useRef, useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';

interface ContextMenuProps {
  x: number;
  y: number;
  options: {
    id: string;
    label: string;
    action: () => void;
    icon?: React.ReactNode;
    isDestructive?: boolean;
  }[];
  onClose: () => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, options, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Focus first item on mount, restore focus on unmount
  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    // Focus first menu item
    requestAnimationFrame(() => {
      const firstBtn = menuRef.current?.querySelector<HTMLElement>('button[role="menuitem"]');
      firstBtn?.focus();
    });
    return () => {
      previousFocusRef.current?.focus();
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Keyboard navigation: Escape, ArrowDown, ArrowUp, Home, End
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('button[role="menuitem"]') ?? []
    );
    const currentIdx = items.indexOf(document.activeElement as HTMLElement);

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
      case 'ArrowDown':
        event.preventDefault();
        items[(currentIdx + 1) % items.length]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        items[(currentIdx - 1 + items.length) % items.length]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        items[items.length - 1]?.focus();
        break;
      case 'Tab':
        // Prevent tab from leaving menu — close instead
        event.preventDefault();
        onClose();
        break;
    }
  }, [onClose]);

  const [adjustedPos, setAdjustedPos] = useState({ x, y });

  // Ensure the menu stays within viewport
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      
      let nextX = x;
      let nextY = y;
      if (rect.right > vw) {
        nextX = Math.max(4, x - rect.width);
      }
      if (rect.bottom > vh) {
        nextY = Math.max(4, y - rect.height);
      }
      setAdjustedPos(prev => (prev.x !== nextX || prev.y !== nextY ? { x: nextX, y: nextY } : prev));
    }
  }, [x, y]);
  
  const style: React.CSSProperties = {
    top: `${adjustedPos.y}px`,
    left: `${adjustedPos.x}px`,
  };

  const menu = (
    <div
      ref={menuRef}
      style={style}
      className="fixed context-menu animate-scale-fade-in"
      role="menu"
      aria-label="Kontextmenü"
      onKeyDown={handleKeyDown}
    >
      <ul className="py-1">
        {options.map((option, idx) => (
          <React.Fragment key={option.id}>
            {option.isDestructive && idx > 0 && (
              <li className="border-t border-rule my-1" role="separator" />
            )}
            <li>
              <button
                onClick={() => {
                  option.action();
                  onClose();
                }}
                className={`context-menu-item w-full text-left ${
                  option.isDestructive ? '!text-red-700 dark:!text-red-400' : ''
                }`}
                role="menuitem"
                tabIndex={-1}
              >
                {option.icon && <span className="w-4 h-4 shrink-0 flex items-center justify-center text-graphite [&_svg]:h-4 [&_svg]:w-4">{option.icon}</span>}
                <span>{option.label}</span>
              </button>
            </li>
          </React.Fragment>
        ))}
      </ul>
    </div>
  );
  
  return ReactDOM.createPortal(menu, document.body);
};

export default ContextMenu;
